interface C103FormHeaderProps {
  apiNumber: string;
  operator: string;
  leaseName: string;
  leaseType: string;
  region: string;
  coaFigure: string;
  wellName?: string;
  county?: string;
  section?: string;
  township?: string;
  range?: string;
  onFieldChange: (field: string, value: string) => void;
}

const LEASE_TYPE_LABELS: Record<string, string> = {
  state: "State",
  fee: "Fee",
  federal: "Federal",
  indian: "Indian",
};

const LEASE_TYPE_COLORS: Record<string, string> = {
  state: "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
  fee: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700",
  federal: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700",
  indian: "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700",
};

const COA_FIGURE_COLORS: Record<string, string> = {
  A: "bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300",
  B: "bg-info-100 text-info-700 dark:bg-info-900/30 dark:text-info-300",
  C: "bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300",
  D: "bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300",
};

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
        {label}
      </label>
      <div className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
        {value || <span className="text-gray-400 dark:text-gray-500">—</span>}
      </div>
    </div>
  );
}

function EditableField({
  label,
  field,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  field: string;
  value: string;
  placeholder?: string;
  onChange: (field: string, value: string) => void;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
      />
    </div>
  );
}

export default function C103FormHeader({
  apiNumber,
  operator,
  leaseName,
  leaseType,
  region,
  coaFigure,
  wellName = "",
  county = "",
  section = "",
  township = "",
  range = "",
  onFieldChange,
}: C103FormHeaderProps) {
  const leaseTypeLabel = LEASE_TYPE_LABELS[leaseType] ?? leaseType;
  const leaseTypeColor = LEASE_TYPE_COLORS[leaseType] ?? LEASE_TYPE_COLORS["fee"];
  const coaColor = COA_FIGURE_COLORS[coaFigure] ?? "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300";

  const locationStr = [section && `Sec. ${section}`, township && `T${township}`, range && `R${range}`]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="rounded-2xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
      {/* Official form header bar */}
      <div className="px-6 py-3 bg-gray-50 dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/[0.05] rounded-t-2xl">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              State of New Mexico — Oil Conservation Division
            </p>
            <p className="text-sm font-bold text-gray-800 dark:text-white/90 mt-0.5">
              Form C-103 — Application to Plug and Abandon
            </p>
          </div>
          <div className="flex items-center gap-2">
            {leaseType && (
              <span className={`text-xs font-semibold px-2 py-1 rounded border ${leaseTypeColor}`}>
                {leaseTypeLabel} Lease
              </span>
            )}
            {coaFigure && (
              <span className={`text-xs font-semibold px-2 py-1 rounded ${coaColor}`}>
                Figure {coaFigure}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-5">
        {/* Filing type checkboxes */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Filing Type
          </p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                onChange={(e) => onFieldChange("filing_noi", e.target.checked ? "true" : "")}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Notice of Intent (NOI)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                onChange={(e) => onFieldChange("filing_subsequent", e.target.checked ? "true" : "")}
                className="w-4 h-4 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Subsequent Report</span>
            </label>
          </div>
        </div>

        {/* Operator and lease */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EditableField
            label="Operator Name"
            field="operator"
            value={operator}
            placeholder="Enter operator name"
            onChange={onFieldChange}
          />
          <EditableField
            label="Lease Name"
            field="leaseName"
            value={leaseName}
            placeholder="Enter lease name"
            onChange={onFieldChange}
          />
        </div>

        {/* Well name and API */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EditableField
            label="Well Name / Number"
            field="wellName"
            value={wellName}
            placeholder="Enter well name"
            onChange={onFieldChange}
          />
          <ReadOnlyField label="API Number" value={apiNumber} />
        </div>

        {/* Lease type selector */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lease Type
            </label>
            <select
              value={leaseType}
              onChange={(e) => onFieldChange("leaseType", e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Select lease type...</option>
              <option value="state">State</option>
              <option value="fee">Fee</option>
              <option value="federal">Federal</option>
              <option value="indian">Indian</option>
            </select>
          </div>
          <ReadOnlyField label="District / Region" value={region ? `${region} District` : ""} />
        </div>

        {/* Location */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
            Location
          </p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <EditableField
              label="County"
              field="county"
              value={county}
              placeholder="e.g., Eddy"
              onChange={onFieldChange}
            />
            <EditableField
              label="Section"
              field="section"
              value={section}
              placeholder="e.g., 15"
              onChange={onFieldChange}
            />
            <EditableField
              label="Township"
              field="township"
              value={township}
              placeholder="e.g., 22S"
              onChange={onFieldChange}
            />
            <EditableField
              label="Range"
              field="range"
              value={range}
              placeholder="e.g., 29E"
              onChange={onFieldChange}
            />
          </div>
          {locationStr && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Location: {locationStr}
            </p>
          )}
        </div>

        {/* COA Figure display */}
        <div className="rounded-lg bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-white/[0.05] px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  COA Figure
                </p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                  {coaFigure ? `Figure ${coaFigure}` : "Not assigned"}
                </p>
              </div>
              <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  District
                </p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
                  {region || "—"}
                </p>
              </div>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              Read-only — set via Region Selector
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
